import Vue from 'vue'
import VueI18n from 'vue-i18n'
import zh from './zh'
import en from './en'
import zhHant from './zhHant'
import ja from './ja'
import ko from './ko'
import es from './es'
import pt from './pt'
import id from './id'
import vi from './vi'
import th from './th'
import ru from './ru'
import ar from './ar'
import fr from './fr'
import de from './de'
import tr from './tr'
import hi from './hi'
import it from './it'
import ms from './ms'
import fil from './fil'

Vue.use(VueI18n)

// 支持的语言列表，name 为母语名称
export const SUPPORTED_LOCALES = [
  { locale: 'en', name: 'English' },
  { locale: 'zh', name: '简体中文' },
  { locale: 'zh-Hant', name: '繁體中文' },
  { locale: 'ja', name: '日本語' },
  { locale: 'ko', name: '한국어' },
  { locale: 'es', name: 'Español' },
  { locale: 'pt', name: 'Português' },
  { locale: 'id', name: 'Bahasa Indonesia' },
  { locale: 'vi', name: 'Tiếng Việt' },
  { locale: 'th', name: 'ไทย' },
  { locale: 'ru', name: 'Русский' },
  { locale: 'ar', name: 'العربية' },
  { locale: 'fr', name: 'Français' },
  { locale: 'de', name: 'Deutsch' },
  { locale: 'tr', name: 'Türkçe' },
  { locale: 'hi', name: 'हिन्दी' },
  { locale: 'it', name: 'Italiano' },
  { locale: 'ms', name: 'Bahasa Melayu' },
  { locale: 'fil', name: 'Filipino' }
]

function isSupportedLocale (locale) {
  return SUPPORTED_LOCALES.some(function (item) { return item.locale === locale })
}

// 从 localStorage 或浏览器语言中获取语言设置
function getLocale () {
  const savedLang = localStorage.getItem('locale')
  if (savedLang && isSupportedLocale(savedLang)) {
    return savedLang
  }
  // 根据浏览器语言自动判断
  const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase()
  if (browserLang.startsWith('zh')) {
    // Safari 等浏览器上报的是 zh-Hant / zh-Hant-TW，也要覆盖到
    if (browserLang.startsWith('zh-tw') || browserLang.startsWith('zh-hk') ||
        browserLang.startsWith('zh-mo') || browserLang.startsWith('zh-hant')) {
      return 'zh-Hant'
    }
    return 'zh'
  }
  const prefix = browserLang.split('-')[0]
  const exact = SUPPORTED_LOCALES.find(function (item) { return item.locale === browserLang })
  if (exact) {
    return exact.locale
  }
  const byPrefix = SUPPORTED_LOCALES.find(function (item) { return item.locale === prefix })
  if (byPrefix) {
    return byPrefix.locale
  }
  return 'en'
}

const i18n = new VueI18n({
  locale: getLocale(),
  // global 产品缺 key 时回退英文，而不是中文
  fallbackLocale: 'en',
  // 俄语/阿拉伯语复数规则与英文不同（如 1 совпадение / 2 совпадения / 5 совпадений），
  // 需按 CLDR 规则选择对应段，配合各语言文件里的多段复数写法
  pluralizationRules: {
    ru: function (choice) {
      const n = Math.abs(choice)
      if (n % 10 === 1 && n % 100 !== 11) return 0 // one
      if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return 1 // few
      return 2 // many（含 0）
    },
    ar: function (choice) {
      const n = Math.abs(choice)
      if (n === 0) return 0 // zero
      if (n === 1) return 1 // one
      if (n === 2) return 2 // two
      if (n >= 3 && n <= 10) return 3 // few
      if (n >= 11 && n <= 99) return 4 // many
      return 5 // other
    }
  },
  messages: {
    zh,
    en,
    'zh-Hant': zhHant,
    ja,
    ko,
    es,
    pt,
    id,
    vi,
    th,
    ru,
    ar,
    fr,
    de,
    tr,
    hi,
    it,
    ms,
    fil
  }
})

/**
 * 获取浏览器/localStorage 推断的语言（不依赖 i18n.locale）
 */
export { getLocale }

// global 版简体中文文案品牌替换：章摇摇→OctoSay、小摇→AI、章鱼群→Octo群组
// cn 版保留原品牌；zh-Hant 文案文件本身就是 OctoSay 品牌，无需处理
let zhBrandingApplied = false
export function applyRegionBranding (region) {
  if (zhBrandingApplied || !region || region === 'cn') return
  zhBrandingApplied = true
  const msgs = i18n.messages.zh
  Object.keys(msgs).forEach(function (key) {
    if (typeof msgs[key] === 'string') {
      msgs[key] = msgs[key].replace(/章摇摇/g, 'OctoSay')
        .replace(/小摇/g, 'AI')
        .replace(/章鱼群/g, 'Octo群组')
    }
  })
}

/**
 * 强制设置语言（优先级高于浏览器语言）
 * 通常由 config.json 中的 region 字段触发
 * 用户手动选择过语言（locale_user_set）时不再覆盖
 * @param {string} locale - 语言代码 (如 'zh', 'en')
 * @param {string} [region] - 区域 (cn/global)，global 时对简中文案做品牌替换
 */
export function setForcedLocale (locale, region) {
  // 品牌替换与用户是否手动选过语言无关，必须在 user_set 守卫之前执行
  applyRegionBranding(region)
  if (localStorage.getItem('locale_user_set') === '1') {
    return
  }
  if (isSupportedLocale(locale)) {
    i18n.locale = locale
    localStorage.setItem('locale', locale)
  }
}

// 暴露到 window 上方便调试
if (typeof window !== 'undefined') {
  window.i18n = i18n
}

export default i18n



// WEBPACK FOOTER //
// ./src/i18n/index.js